import { startOfMonth, endOfMonth, subMonths, subDays, startOfWeek, endOfWeek, subWeeks } from "date-fns";
import {
  getPaymentTimestampMs,
  isSuccessfulPayment,
  paymentAmountUsd,
  monthlyRevenueSeries,
} from "./metrics";
import { filterPayments } from "./dataQualityFilters";

function bucketPayments(payments, contacts, filters, excludedIds, buckets) {
  const { included } = filterPayments(payments, contacts, filters, excludedIds);
  const totals = buckets.map(() => 0);
  for (const p of included) {
    const t = getPaymentTimestampMs(p);
    if (!t) continue;
    const amt = paymentAmountUsd(p);
    for (let i = 0; i < buckets.length; i++) {
      const { start, end } = buckets[i];
      if (t >= start && t <= end) {
        totals[i] += amt;
        break;
      }
    }
  }
  return totals;
}

/** Build revenue chart data that actually changes per D/W/M/Q/Y toggle. */
export function buildRevenueChartSeries(payments, contacts, periodId, filters, excludedIds = new Set()) {
  const now = new Date();

  if (periodId === "monthly") {
    const { labels, totals } = monthlyRevenueSeries(payments, contacts, 12);
    const { included } = filterPayments(payments, contacts, filters, excludedIds);
    const filtered = totals.map((_, i) => {
      const monthStarts = [];
      for (let j = 11; j >= 0; j--) monthStarts.push(startOfMonth(subMonths(now, j)));
      const start = monthStarts[i].getTime();
      const end = endOfMonth(monthStarts[i]).getTime();
      return included.reduce((s, p) => {
        const t = getPaymentTimestampMs(p);
        if (t >= start && t <= end) return s + paymentAmountUsd(p);
        return s;
      }, 0);
    });
    return { labels, values: filtered };
  }

  if (periodId === "daily") {
    const buckets = [];
    const labels = [];
    for (let i = 29; i >= 0; i--) {
      const d = subDays(now, i);
      const start = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
      const end = new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).getTime();
      buckets.push({ start, end });
      labels.push(d.toLocaleDateString(undefined, { month: "short", day: "numeric" }));
    }
    return { labels, values: bucketPayments(payments, contacts, filters, excludedIds, buckets) };
  }

  if (periodId === "weekly") {
    const buckets = [];
    const labels = [];
    for (let i = 11; i >= 0; i--) {
      const wEnd = endOfWeek(subWeeks(now, i), { weekStartsOn: 1 });
      const wStart = startOfWeek(subWeeks(now, i), { weekStartsOn: 1 });
      buckets.push({ start: wStart.getTime(), end: wEnd.getTime() });
      labels.push(wStart.toLocaleDateString(undefined, { month: "short", day: "numeric" }));
    }
    return { labels, values: bucketPayments(payments, contacts, filters, excludedIds, buckets) };
  }

  if (periodId === "quarterly") {
    const buckets = [];
    const labels = [];
    for (let i = 7; i >= 0; i--) {
      const ref = subMonths(now, i * 3);
      const q = Math.floor(ref.getMonth() / 3);
      const start = new Date(ref.getFullYear(), q * 3, 1).getTime();
      const end = endOfMonth(new Date(ref.getFullYear(), q * 3 + 2, 1)).getTime();
      buckets.push({ start, end });
      labels.push(`${ref.getFullYear()} Q${q + 1}`);
    }
    return { labels, values: bucketPayments(payments, contacts, filters, excludedIds, buckets) };
  }

  if (periodId === "yearly") {
    const buckets = [];
    const labels = [];
    for (let i = 4; i >= 0; i--) {
      const y = now.getFullYear() - i;
      buckets.push({ start: new Date(y, 0, 1).getTime(), end: new Date(y, 11, 31, 23, 59, 59, 999).getTime() });
      labels.push(String(y));
    }
    return { labels, values: bucketPayments(payments, contacts, filters, excludedIds, buckets) };
  }

  return monthlyRevenueSeries(payments, contacts, 12);
}
